import type { DataOption } from '@/entities/column'
import type { interpretationLimitationsWidgetSchema } from '@/entities/optimization-statistic'
import type z from 'zod'

import { useCallback } from 'react'
import { find, isNil } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import ChartView from './ChartView'

const colors = [
	'rgba(115, 120, 237, 1)',
	'rgba(50, 178, 243, 1)',
	'rgba(255, 152, 0, 0.5)',
	'rgba(0, 181, 87, 0.5)',
	'rgba(63, 81, 181, 0.5)',
	'rgba(244, 66, 54, 0.5)',
	'rgba(50, 178, 243, 0.5)',
	'#142A45',
]
export default function Limitations(
	props: z.infer<typeof interpretationLimitationsWidgetSchema> & {
		options: {
			column: DataOption[]
			anchor_type: DataOption[]
		}
	}
) {
	const { t } = useTranslation()

	const series = props.series.map((serie, i) => ({
		...serie,
		color: colors[i],
	}))
	const plotBandFrom = isNil(props.plotBand.from) ? 0 : props.plotBand.from
	const plotBandTo = props.plotBand.to
	const isLowerLimitHigher = !isNil(plotBandTo) && plotBandFrom > plotBandTo
	const plotBand = {
		...props.plotBand,
		color: isLowerLimitHigher ? '#FBD1CE' : '#C4F2DB',
	}
	const getLabel = useCallback(
		(value: string) => {
			const label =
				find({ value }, props.options.column)?.label ||
				find({ value }, props.options.anchor_type)?.label ||
				value

			return t(label, { ns: 'fields', defaultValue: label })
		},
		[props.options, t]
	)

	return (
		<div>
			<ChartView
				{...props}
				series={series}
				plotBand={plotBand}
				getLabel={getLabel}
			/>

			<div>
				{!isNil(props.last_price) && (
					<div
						key='last price'
						className='mt-1 flex items-center justify-between'
					>
						<div className='text-xs'>
							<span className='mr-2 inline-block size-2 rounded-full bg-[#E87B35]' />
							<span className='font-medium text-foreground'>
								{t('widget.current_price', { defaultValue: 'Current price' })}
							</span>
						</div>
						<div>
							<span className='font-mono text-xs font-semibold tabular-nums'>{`${formatNumber(props.last_price, NumberFormats.Fixed)}`}</span>
						</div>
					</div>
				)}
				<div
					key='price_interval'
					className='mt-1 flex items-center justify-between'
				>
					{isLowerLimitHigher ? (
						<div className='text-xs'>
							<span className='mr-2 inline-block size-2 rounded-full bg-[#FBD1CE]' />
							<span className='font-medium text-foreground'>
								{t('widget.limit_conflict_interval', {
									defaultValue: 'Limit conflict interval',
								})}
							</span>
						</div>
					) : (
						<div className='text-xs'>
							<span className='mr-2 inline-block size-2 rounded-full bg-[#C4F2DB]' />
							<span className='font-medium text-foreground'>
								{t('widget.available_price_interval', {
									defaultValue: 'Available price interval',
								})}
							</span>
						</div>
					)}
					<div>
						<span className='font-mono text-xs font-semibold tabular-nums'>
							{`${formatNumber(plotBandFrom, NumberFormats.Fixed)}`}
							{' - '}
						</span>
						<span
							className={cn(
								'font-mono text-xs font-semibold tabular-nums',
								isNil(plotBandTo) && 'text-muted-foreground'
							)}
						>
							{isNil(plotBandTo)
								? 'Infinity'
								: `${formatNumber(plotBandTo, NumberFormats.Fixed)}`}
						</span>
					</div>
				</div>
				{series.map((serie) => {
					const label = getLabel(serie.name)
					const hasFrom = !isNil(serie?.range[0])
					const hasTo = !isNil(serie?.range[1])
					const type =
						serie.type === 'highlight'
							? t('highlight', { defaultValue: 'Highlight' })
							: t('correct', { defaultValue: 'Correct' })

					return (
						<div key={serie.name} className='mt-1 flex justify-between'>
							<div>
								<span
									className='mr-2 inline-block size-2 rounded-full'
									style={{ background: serie.color }}
								/>
								<span className='text-xs font-medium text-foreground'>
									{label}{' '}
									{t('widget.serie.limit', { defaultValue: 'limit' })}{' '}
								</span>
								{' - '}
								<span className='text-xs text-muted-foreground'>{type}</span>
							</div>
							<div className='text-sm text-nowrap'>
								<span
									className={cn(
										'font-mono text-xs font-semibold tabular-nums',
										!hasFrom && 'text-muted-foreground'
									)}
								>
									{!hasFrom
										? 'Infinity'
										: `${formatNumber(serie.range[0]!, NumberFormats.Fixed)}`}
								</span>
								{' - '}
								<span
									className={cn(
										'font-mono text-xs font-semibold tabular-nums',
										!hasTo && 'text-muted-foreground'
									)}
								>
									{!hasTo
										? 'Infinity'
										: `${formatNumber(serie.range[1]!, NumberFormats.Fixed)}`}
								</span>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
